import { createTheme, type PaletteMode, type ThemeOptions } from "@mui/material";

const brandPalette = {
  gold: "#C9A35A",
  goldDark: "#A98544",
  blush: "#F6E3DA",
  cream: "#FAF4EC",
  pearl: "#FFFDFC",
  charcoal: "#2C2A28",
  stone: "#6E6A66",
} as const;

export function buildTheme(mode: PaletteMode) {
  const isDark = mode === "dark";

  const palette: ThemeOptions["palette"] = {
    mode,
    primary: {
      main: isDark ? brandPalette.gold : brandPalette.goldDark,
      contrastText: isDark ? brandPalette.charcoal : "#FFFFFF",
    },
    secondary: {
      main: isDark ? brandPalette.blush : brandPalette.blush,
      contrastText: brandPalette.charcoal,
    },
    background: {
      default: isDark ? "#151311" : brandPalette.cream,
      paper: isDark ? "#201C18" : brandPalette.pearl,
    },
    text: {
      primary: isDark ? "#F4EEE5" : brandPalette.charcoal,
      secondary: isDark ? "#BFB7AB" : brandPalette.stone,
    },
    divider: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
  };

  return createTheme({
    palette,
    shape: { borderRadius: 18 },
    typography: {
      fontFamily: [
        '"Playfair Display"',
        '"Cormorant Garamond"',
        "Georgia",
        "serif",
      ].join(","),
      h1: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, letterSpacing: -0.5 },
      h2: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, letterSpacing: -0.3 },
      h3: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 600, letterSpacing: -0.2 },
      h4: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 600 },
      h5: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500 },
      h6: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500 },
      body1: { fontFamily: '"Inter", "Helvetica", "Arial", sans-serif', fontSize: "1.05rem", lineHeight: 1.75 },
      body2: { fontFamily: '"Inter", "Helvetica", "Arial", sans-serif', lineHeight: 1.6 },
      button: { fontFamily: '"Inter", "Helvetica", "Arial", sans-serif', fontWeight: 600, textTransform: "none", letterSpacing: 0.3 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 999, paddingInline: 24, paddingBlock: 10 },
          containedPrimary: {
            boxShadow: "0 8px 24px -12px rgba(201,163,90,0.5)",
            ":hover": { boxShadow: "0 12px 28px -12px rgba(201,163,90,0.65)" },
          },
          outlined: {
            borderWidth: 1.5,
          },
        },
      },
      MuiAppBar: {
        defaultProps: { color: "transparent", elevation: 0 },
        styleOverrides: {
          root: {
            backdropFilter: "saturate(180%) blur(10px)",
            backgroundColor: isDark ? "rgba(20,18,16,0.72)" : "rgba(255,252,247,0.8)",
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(169,133,68,0.15)"}`,
            boxShadow: isDark
              ? "0 1px 0 rgba(255,255,255,0.03) inset, 0 20px 36px -26px rgba(0,0,0,0.5)"
              : "0 1px 2px rgba(44,42,40,0.05), 0 16px 34px -24px rgba(44,42,40,0.24)",
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: "none" } },
      },
    },
  });
}

export { brandPalette };
